import clsx from 'clsx';
import type { ComponentProps, ReactNode, Ref } from 'react';
import { useId, useRef, useState } from 'react';

import * as styles from './animated-disclosure.css';
import { AnimatedRegion } from './animated-region';
import { DisclosureChevron } from './disclosure-chevron';

/** Shared disclosure trigger, animated region and keyboard/focus behavior. */
export function AnimatedDisclosure({
  title,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  actions,
  renderHeader,
  keepMounted,
  className,
  headerClassName,
  headerRef,
  headerProps,
  triggerClassName,
  regionClassName,
  regionStyle,
  animate = true,
  animateContent = true,
  preserveHeightDuringCollapse = false,
  children,
  ...props
}: Omit<ComponentProps<'div'>, 'title' | 'children' | 'onChange'> & {
  title: ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  actions?: ReactNode;
  renderHeader?: (header: ReactNode) => ReactNode;
  keepMounted?: boolean;
  headerClassName?: string;
  headerRef?: Ref<HTMLDivElement>;
  headerProps?: ComponentProps<'div'> & {
    [attribute: `data-${string}`]: string | boolean | undefined;
  };
  triggerClassName?: string;
  regionClassName?: string;
  regionStyle?: ComponentProps<'div'>['style'];
  animate?: boolean;
  animateContent?: boolean;
  preserveHeightDuringCollapse?: boolean;
  children: ReactNode;
}) {
  const [localExpanded, setExpanded] = useState(defaultExpanded);
  const expanded = controlledExpanded ?? localExpanded;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const regionId = useId();
  const triggerId = `${regionId}-trigger`;
  const header = (
    <div
      {...headerProps}
      ref={headerRef}
      className={clsx(styles.header, headerClassName)}
    >
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        className={clsx(styles.trigger, triggerClassName)}
        aria-expanded={expanded}
        aria-controls={regionId}
        onClick={() => {
          if (controlledExpanded === undefined) setExpanded(!expanded);
          onExpandedChange?.(!expanded);
        }}
      >
        <DisclosureChevron expanded={expanded} animate={animate} />
        {title}
      </button>
      {actions}
    </div>
  );
  return (
    <div {...props} className={clsx(styles.root, className)}>
      {renderHeader ? renderHeader(header) : header}
      <AnimatedRegion
        keepMounted={keepMounted}
        id={regionId}
        expanded={expanded}
        animate={animate}
        animateContent={animateContent}
        preserveHeightDuringCollapse={preserveHeightDuringCollapse}
        className={regionClassName}
        style={regionStyle}
        aria-hidden={!expanded}
        returnFocusRef={triggerRef}
        role="region"
        aria-labelledby={triggerId}
      >
        {children}
      </AnimatedRegion>
    </div>
  );
}
